import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CartLine, DeliveryMethod, PricedCart } from '@chicare/shared';

import { api } from './api';

/**
 * The cart.
 *
 * Deliberately NOT stored on the server. A cart is a list of listing ids and
 * quantities the client keeps; the server turns it into money on demand. That
 * buys three things:
 *
 *   - a buyer can fill a cart before they ever hand over a phone number
 *   - the price and the stock are re-checked every single time the cart is
 *     opened, not once at checkout
 *   - React Native swaps `localStorage` for `AsyncStorage` and nothing else
 *     changes
 *
 * It holds ids and quantities and NOTHING else. No prices, no names, no totals.
 * A cached price is a price that can be wrong, and a wrong price in a pharmacy
 * is a buyer arriving at a doorstep expecting to pay something they will not be
 * asked for.
 */

const STORAGE_KEY = 'chicare.cart';

type Listener = () => void;

let lines: CartLine[] = read();
const listeners = new Set<Listener>();

function read(): CartLine[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];

    return Array.isArray(parsed)
      ? parsed.filter(
          (line): line is CartLine =>
            typeof line?.listing_id === 'number' && typeof line?.quantity === 'number',
        )
      : [];
  } catch {
    // Private mode, a cleared store, or something that is not our JSON.
    // An unreadable cart is an empty cart, never a crash on first paint.
    return [];
  }
}

function commit(next: CartLine[]): void {
  lines = next;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage is full or blocked. The cart still works for this session.
  }

  listeners.forEach((listener) => listener());
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);

  return () => listeners.delete(listener);
}

/** Another tab changed the cart. Two tabs disagreeing about a cart is a bug. */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) {
      lines = read();
      listeners.forEach((listener) => listener());
    }
  });
}

export const cartStore = {
  subscribe,
  get: () => lines,

  add(listingId: number, quantity = 1): void {
    const existing = lines.find((line) => line.listing_id === listingId);

    commit(
      existing
        ? lines.map((line) =>
            line.listing_id === listingId
              ? { ...line, quantity: Math.min(99, line.quantity + quantity) }
              : line,
          )
        : [...lines, { listing_id: listingId, quantity }],
    );
  },

  setQuantity(listingId: number, quantity: number): void {
    commit(
      quantity <= 0
        ? lines.filter((line) => line.listing_id !== listingId)
        : lines.map((line) =>
            line.listing_id === listingId ? { ...line, quantity: Math.min(99, quantity) } : line,
          ),
    );
  },

  remove(listingId: number): void {
    commit(lines.filter((line) => line.listing_id !== listingId));
  },

  clear(): void {
    commit([]);
  },
};

export function useCartLines(): CartLine[] {
  return useSyncExternalStore(subscribe, cartStore.get, () => []);
}

/** Total items, for the badge on the cart tab. */
export function useCartCount(): number {
  const lines = useCartLines();

  return useMemo(() => lines.reduce((total, line) => total + line.quantity, 0), [lines]);
}

/**
 * The cart, priced by the server.
 *
 * `staleTime: 0` on purpose. A cart is the one screen where a stale price is
 * actively harmful, and re-pricing is one cheap request.
 */
export function usePricedCart(options: {
  deliveryMethod?: DeliveryMethod;
  creditKobo?: number;
} = {}) {
  const lines = useCartLines();
  const { deliveryMethod = 'standard', creditKobo = 0 } = options;

  return useQuery<PricedCart>({
    queryKey: ['cart', lines, deliveryMethod, creditKobo],
    queryFn: () => api.priceCart({ items: lines, deliveryMethod, creditKobo }),
    enabled: lines.length > 0,
    staleTime: 0,
  });
}

/**
 * One idempotency key per checkout ATTEMPT (§9.14).
 *
 * Generated when the buyer opens checkout and reused for every retry of that
 * attempt — a fresh key per tap is precisely the bug idempotency exists to
 * prevent. It is cleared once an order is successfully placed.
 */
const IDEMPOTENCY_KEY = 'chicare.checkout-key';

export function useCheckoutKey(): { key: string; reset: () => void } {
  const key = useMemo(() => {
    try {
      const existing = window.sessionStorage.getItem(IDEMPOTENCY_KEY);
      if (existing) return existing;

      const fresh = crypto.randomUUID();
      window.sessionStorage.setItem(IDEMPOTENCY_KEY, fresh);

      return fresh;
    } catch {
      return crypto.randomUUID();
    }
  }, []);

  const reset = useCallback(() => {
    try {
      window.sessionStorage.removeItem(IDEMPOTENCY_KEY);
    } catch {
      // Nothing to clean up.
    }
  }, []);

  return { key, reset };
}

/** Drop the cart and the checkout key once an order exists. */
export function useClearCartOnSuccess(placed: boolean, reset: () => void): void {
  useEffect(() => {
    if (placed) {
      cartStore.clear();
      reset();
    }
  }, [placed, reset]);
}
